import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { CalendarPlus, Copy, Loader2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Button } from '../ui/button'
import { Skeleton } from '../ui/skeleton'
import { exercisesService } from '../../services/exercises'
import type { Exercise } from '../../types/exercise'

interface CreateCustomExerciseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Puts an existing exercise straight on the diary. Omitted where there is no
   * diary to add it to (the "my workouts" list passes it, the plan builder
   * would not).
   */
  onSchedule?: (exercise: Exercise) => Promise<void>
}

const MAX_SUGGESTIONS = 6
const MIN_QUERY = 2

/**
 * The way into the custom-exercise editor. Typing a title searches the catalog
 * first: most of what people are about to write already exists, and the two
 * useful answers to "this one is close" are to schedule it as is, or to open
 * the editor pre-filled with its blocks and change what differs. Creating from
 * scratch stays one click away either way.
 */
export function CreateCustomExerciseDialog({
  open,
  onOpenChange,
  onSchedule,
}: CreateCustomExerciseDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [title, setTitle] = useState('')
  const [suggestions, setSuggestions] = useState<Exercise[]>([])
  const [loading, setLoading] = useState(false)
  const [schedulingId, setSchedulingId] = useState<string | null>(null)
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!open) {
      setTitle('')
      setSuggestions([])
      setSchedulingId(null)
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    const query = title.trim()
    if (query.length < MIN_QUERY) {
      setSuggestions([])
      setLoading(false)
      return
    }

    if (searchTimer.current) clearTimeout(searchTimer.current)
    setLoading(true)
    searchTimer.current = setTimeout(async () => {
      try {
        const results = await exercisesService.search({ q: query })
        setSuggestions(results.slice(0, MAX_SUGGESTIONS))
      } catch {
        setSuggestions([])
      } finally {
        setLoading(false)
      }
    }, 300)

    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current)
    }
  }, [title, open])

  const trimmed = title.trim()

  function handleCreate() {
    if (!trimmed) return
    onOpenChange(false)
    navigate(`/training-diary/exercises/new?title=${encodeURIComponent(trimmed)}`)
  }

  function handleDuplicate(exercise: Exercise) {
    onOpenChange(false)
    navigate(`/training-diary/exercises/new?duplicate_from=${exercise.id}`)
  }

  async function handleSchedule(exercise: Exercise) {
    if (!onSchedule) return
    setSchedulingId(exercise.id)
    try {
      await onSchedule(exercise)
      onOpenChange(false)
    } finally {
      setSchedulingId(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {t('trainingDiary.customExercise.dialog.title', 'New custom workout')}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-x-2">
            {t(
              'trainingDiary.customExercise.dialog.description',
              'Only you can see your custom workouts.'
            )}
            {/* The only standing way back to the management list. */}
            <button
              type="button"
              className="underline underline-offset-2 hover:text-foreground transition-colors"
              onClick={() => {
                onOpenChange(false)
                navigate('/training-diary/exercises')
              }}
            >
              {t('trainingDiary.customExercise.dialog.manage', 'Manage mine')}
            </button>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="custom-exercise-title" className="text-xs">
            {t('trainingDiary.customExercise.dialog.titleLabel', 'Title')}
          </Label>
          <Input
            id="custom-exercise-title"
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleCreate()
              }
            }}
            placeholder={t(
              'trainingDiary.customExercise.dialog.titlePlaceholder',
              'e.g. Max hangs 92%'
            )}
          />
        </div>

        <div className="flex flex-col gap-2 overflow-y-auto -mx-1 px-1 min-h-[120px]">
          {loading && (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 3 }, (_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-md" />
              ))}
            </div>
          )}

          {!loading && trimmed.length >= MIN_QUERY && suggestions.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-6">
              {t(
                'trainingDiary.customExercise.dialog.noMatches',
                'Nothing like it yet — this will be a brand new workout.'
              )}
            </p>
          )}

          {!loading && suggestions.length > 0 && (
            <>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {t('trainingDiary.customExercise.dialog.existingLabel', 'Already exists')}
              </p>
              <ul className="flex flex-col gap-1.5">
                {suggestions.map((exercise) => (
                  <li
                    key={exercise.id}
                    className="flex items-center gap-2 rounded-md border p-2 bg-card"
                  >
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">
                        {exercise.category.name}
                      </span>
                      <span className="text-sm truncate">{exercise.title}</span>
                    </div>

                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-xs shrink-0"
                      onClick={() => handleDuplicate(exercise)}
                    >
                      <Copy className="size-3.5 mr-1" />
                      {t('trainingDiary.customExercise.dialog.duplicate', 'Duplicate')}
                    </Button>

                    {onSchedule && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 px-2 text-xs shrink-0"
                        disabled={schedulingId != null}
                        aria-label={t('trainingDiary.customExercise.dialog.schedule', 'Add to diary')}
                        onClick={() => handleSchedule(exercise)}
                      >
                        {schedulingId === exercise.id ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          <CalendarPlus className="size-3.5" />
                        )}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button type="button" disabled={!trimmed} onClick={handleCreate}>
            {t('trainingDiary.customExercise.dialog.create', 'Create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
